import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import {
  reportService,
  MonthlyCaseBillabilityCase,
  MonthlyCaseBillabilitySummary,
  MonthlyCaseBillabilityFilters,
  MonthlyCaseBillabilityPeriod,
  CaseInvoicedStatus,
} from '../services/reportService';
import { useAuth } from '../contexts/AuthContext';

const getPreviousMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

const headerCellSx = {
  backgroundColor: '#eef5ff',
  border: '1px solid #d6e6ff',
  fontWeight: 'bold',
  padding: '10px',
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = { border: '1px solid #d6e6ff', padding: '8px' };
const totalCellSx = { ...bodyCellSx, backgroundColor: '#f8faff', fontWeight: 'bold' };

const formatCurrency = (amount: number | null | undefined): string =>
  amount != null ? `$${amount.toFixed(2)}` : '—';

const invoicedStatusLabel: Record<CaseInvoicedStatus, string> = {
  fully_invoiced: 'Fully Invoiced',
  partially_invoiced: 'Partially Invoiced',
  not_invoiced: 'Not Invoiced',
  none: 'No Billable Time',
};

const invoicedStatusColor: Record<CaseInvoicedStatus, 'success' | 'warning' | 'error' | 'default'> = {
  fully_invoiced: 'success',
  partially_invoiced: 'warning',
  not_invoiced: 'error',
  none: 'default',
};

interface CaseRowProps {
  row: MonthlyCaseBillabilityCase;
  expanded: boolean;
  onToggle: () => void;
  navigate: ReturnType<typeof useNavigate>;
  limited?: boolean;
}

const CaseRow: React.FC<CaseRowProps> = ({ row, expanded, onToggle, navigate, limited }) => (
  <>
    <TableRow hover sx={{ '& > *': { borderBottom: expanded && !limited ? 'unset' : undefined } }}>
      {!limited && (
        <TableCell sx={bodyCellSx}>
          <IconButton size="small" onClick={onToggle} aria-label={expanded ? 'collapse' : 'expand'}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
      )}
      <TableCell sx={bodyCellSx}>
        {row.client_firm_id ? (
          <Button
            size="small"
            sx={{ p: 0, textAlign: 'left', color: '#2563eb', textDecoration: 'underline', textTransform: 'none', minWidth: 0 }}
            onClick={() => navigate(`/firms/${row.client_firm_id}/cases/${row.case_id}`)}
          >
            {row.case_name}
          </Button>
        ) : (
          row.case_name
        )}
      </TableCell>
      <TableCell sx={bodyCellSx}>{row.file_number || '—'}</TableCell>
      <TableCell sx={bodyCellSx}>{row.client}</TableCell>
      <TableCell sx={bodyCellSx}>{row.project_name}</TableCell>
      <TableCell sx={bodyCellSx} align="right">
        {row.hourly_rate != null ? formatCurrency(row.hourly_rate) + '/hr' : '—'}
      </TableCell>
      {!limited && (
        <TableCell sx={bodyCellSx}>
          <Typography variant="body2">{row.clock_human}</Typography>
          <Typography variant="caption" color="text.secondary">{row.clock_formatted}</Typography>
        </TableCell>
      )}
      <TableCell sx={bodyCellSx}>
        <Typography variant="body2">{row.billable_human}</Typography>
        <Typography variant="caption" color="text.secondary">{row.billable_formatted}</Typography>
      </TableCell>
      {!limited && (
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(row.billable_percent ?? 0, 100)}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
              color={(row.billable_percent ?? 0) >= 80 ? 'success' : (row.billable_percent ?? 0) >= 50 ? 'warning' : 'inherit'}
            />
            <Typography variant="caption">{row.billable_percent ?? 0}%</Typography>
          </Box>
        </TableCell>
      )}
      <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.billable_amount)}</TableCell>
      <TableCell sx={bodyCellSx} align="right">{formatCurrency(row.uninvoiced_amount)}</TableCell>
      <TableCell sx={bodyCellSx}>
        <Chip
          size="small"
          label={invoicedStatusLabel[row.invoiced_status]}
          color={invoicedStatusColor[row.invoiced_status]}
          variant="outlined"
        />
      </TableCell>
    </TableRow>
    {!limited && (
    <TableRow>
      <TableCell sx={{ py: 0, border: '1px solid #d6e6ff' }} colSpan={12}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, bgcolor: '#fafcff' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Timer Entries ({row.entry_count ?? 0})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>User</TableCell>
                  <TableCell sx={headerCellSx}>Activity</TableCell>
                  <TableCell sx={headerCellSx}>Billable Activities</TableCell>
                  <TableCell sx={headerCellSx}>Started</TableCell>
                  <TableCell sx={headerCellSx}>Stopped</TableCell>
                  <TableCell sx={headerCellSx}>Clock</TableCell>
                  <TableCell sx={headerCellSx}>Billable</TableCell>
                  <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  <TableCell sx={headerCellSx}>Invoiced</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(row.entries || []).map((entry) => (
                  <TableRow key={entry.timer_id}>
                    <TableCell sx={bodyCellSx}>{entry.user}</TableCell>
                    <TableCell sx={bodyCellSx}>{entry.activity_name}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {entry.billable_activity_names?.length > 0
                        ? entry.billable_activity_names.join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{entry.start_time}</TableCell>
                    <TableCell sx={bodyCellSx}>{entry.end_time}</TableCell>
                    <TableCell sx={bodyCellSx}>{entry.clock_human}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {entry.is_billable ? entry.billable_human : '—'}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="right">
                      {entry.is_billable ? formatCurrency(entry.billable_amount) : '—'}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {entry.is_billable ? (
                        <Chip
                          size="small"
                          label={entry.is_invoiced ? 'Yes' : 'No'}
                          color={entry.is_invoiced ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
    )}
  </>
);

const SummaryCard: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const MonthlyCaseBillabilityReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isPmpAdmin = () => {
    if (!user) return false;
    return !!(user as any).roles?.some((r: any) => r.slug === 'pmp_admin');
  };

  const isFinanceUser = () => {
    if (!user) return false;
    const hasFinanceRole = user.roles?.some((r: any) => r.slug === 'client_finance_user');
    return hasFinanceRole || user.is_finance_user === true;
  };

  const isAuthorized = () => isPmpAdmin() || isFinanceUser();
  const isLimited = isFinanceUser() && !isPmpAdmin();

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const [month, setMonth] = useState<string>(getPreviousMonth());
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [userId, setUserId] = useState<number | ''>('');
  const [invoicedStatus, setInvoicedStatus] = useState<'all' | 'fully_invoiced' | 'partially_invoiced' | 'not_invoiced'>('all');

  const [filters, setFilters] = useState<MonthlyCaseBillabilityFilters>({
    client_firms: [],
    projects: [],
    users: [],
  });

  const [cases, setCases] = useState<MonthlyCaseBillabilityCase[]>([]);
  const [summary, setSummary] = useState<MonthlyCaseBillabilitySummary | null>(null);
  const [period, setPeriod] = useState<MonthlyCaseBillabilityPeriod | null>(null);
  const [expandedCases, setExpandedCases] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'summary' | 'detail' | null>(null);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (isAuthorized()) {
      (reportService as any)
        .getMonthlyCaseBillabilityReportFilters()
        .then((data: MonthlyCaseBillabilityFilters) => {
          setFilters(data);
          if (isLimited && data.client_firms.length === 1) {
            setClientFirmId(data.client_firms[0].id);
          }
        })
        .catch(() => setError('Failed to load filter options'));
    }
  }, [user]);

  const filteredProjects = clientFirmId
    ? filters.projects.filter((p) => p.firm_id === clientFirmId)
    : filters.projects;

  const buildParams = () => {
    const params: Record<string, unknown> = {};
    if (month) params.month = month;
    if (clientFirmId) params.client_firm_id = clientFirmId;
    if (projectId) params.project_id = projectId;
    if (userId && !isLimited) params.user_id = userId;
    if (invoicedStatus !== 'all') params.invoiced_status = invoicedStatus;
    return params;
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setSearched(false);
    setExpandedCases(new Set());
    try {
      const result = await (reportService as any).getMonthlyCaseBillabilityReport(buildParams()) as {
        data: { cases: MonthlyCaseBillabilityCase[]; summary: MonthlyCaseBillabilitySummary };
        period: MonthlyCaseBillabilityPeriod;
      };
      setCases(result.data.cases);
      setSummary(result.data.summary);
      setPeriod(result.period);
      setSearched(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: 'summary' | 'detail') => {
    setExporting(type);
    setError('');
    try {
      const blob = await (reportService as any).getMonthlyCaseBillabilityReport({
        ...buildParams(),
        export: 'csv',
        detail: type === 'detail' ? '1' : '0',
      }) as Blob;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        type === 'detail'
          ? `monthly-case-billability-detail_${month || 'all'}.csv`
          : `monthly-case-billability_${month || 'all'}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export report');
    } finally {
      setExporting(null);
    }
  };

  const toggleCase = (caseId: number) => {
    setExpandedCases((prev) => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const toggleAll = () => {
    if (expandedCases.size === cases.length) {
      setExpandedCases(new Set());
    } else {
      setExpandedCases(new Set(cases.map((c) => c.case_id)));
    }
  };

  const sortedCases = useMemo(
    () => [...cases].sort((a, b) => b.billable_amount - a.billable_amount),
    [cases]
  );

  if (!isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Monthly Case Billability Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isLimited
          ? 'Billable time and invoicing status for your firm\'s time-based cases.'
          : 'Case-wise clock vs billable time for time-based projects, with project hourly rates and invoicing status.'}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: { xs: '100%', md: '180px' } }}>
            <TextField
              fullWidth
              label="Month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 7) }}
            />
          </Box>
          {!(isLimited && filters.client_firms.length <= 1) && (
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select value={clientFirmId} label="Client" onChange={(e) => { setClientFirmId(e.target.value as number | ''); setProjectId(''); }}>
                <MenuItem value="">{isLimited ? 'My Firms' : 'All Clients'}</MenuItem>
                {filters.client_firms.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          )}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select value={projectId} label="Project" onChange={(e) => setProjectId(e.target.value as number | '')} disabled={filteredProjects.length === 0}>
                <MenuItem value="">All Projects</MenuItem>
                {filteredProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}{p.hourly_rate != null ? ` ($${p.hourly_rate.toFixed(2)}/hr)` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {!isLimited && (
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '160px' } }}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select value={userId} label="User" onChange={(e) => setUserId(e.target.value as number | '')}>
                <MenuItem value="">All Users</MenuItem>
                {filters.users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          )}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '180px' } }}>
            <FormControl fullWidth>
              <InputLabel>Invoiced Status</InputLabel>
              <Select value={invoicedStatus} label="Invoiced Status" onChange={(e) => setInvoicedStatus(e.target.value as typeof invoicedStatus)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="fully_invoiced">Fully Invoiced</MenuItem>
                <MenuItem value="partially_invoiced">Partially Invoiced</MenuItem>
                <MenuItem value="not_invoiced">Not Invoiced</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
            <Button fullWidth variant="contained" onClick={handleSearch} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />} sx={{ height: '56px' }}>
              Submit
            </Button>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '150px' } }}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('summary')} disabled={!!exporting || !searched}
              startIcon={exporting === 'summary' ? <CircularProgress size={20} /> : <DownloadIcon />} sx={{ height: '56px' }}>
              Export{isLimited ? '' : ' Summary'}
            </Button>
          </Box>
          {!isLimited && (
          <Box sx={{ minWidth: { xs: '100%', md: '140px' } }}>
            <Button fullWidth variant="outlined" onClick={() => handleExport('detail')} disabled={!!exporting || !searched}
              startIcon={exporting === 'detail' ? <CircularProgress size={20} /> : <DownloadIcon />} sx={{ height: '56px' }}>
              Export Detail
            </Button>
          </Box>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {searched && summary && period && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Period: {period.month_name}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Cases" value={String(summary.case_count)} sub={isLimited ? undefined : `${summary.entry_count} entries`} />
            </Box>
            {!isLimited && (
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Clock Time" value={summary.clock_human || '—'} sub={summary.clock_formatted} />
            </Box>
            )}
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Billable Time" value={summary.billable_human} sub={isLimited ? undefined : `${summary.billable_percent ?? 0}% of clock`} />
            </Box>
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Total Billable" value={formatCurrency(summary.total_billable_amount)} />
            </Box>
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Invoiced" value={formatCurrency(summary.invoiced_amount)} />
            </Box>
            <Box sx={{ flex: '1 1 140px', minWidth: 140 }}>
              <SummaryCard label="Uninvoiced" value={formatCurrency(summary.uninvoiced_amount)} />
            </Box>
          </Box>

          {summary.by_project.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>By Project</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Project</TableCell>
                    <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                    <TableCell sx={headerCellSx} align="right">Cases</TableCell>
                    {!isLimited && <TableCell sx={headerCellSx}>Clock</TableCell>}
                    <TableCell sx={headerCellSx}>Billable</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.by_project.map((p) => (
                    <TableRow key={p.project_id}>
                      <TableCell sx={bodyCellSx}>{p.project_name}</TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        {p.hourly_rate != null ? formatCurrency(p.hourly_rate) + '/hr' : '—'}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">{p.case_count}</TableCell>
                      {!isLimited && (
                      <TableCell sx={bodyCellSx}>
                        {Math.floor((p.clock_seconds ?? 0) / 3600)}h {Math.floor(((p.clock_seconds ?? 0) % 3600) / 60)}m
                      </TableCell>
                      )}
                      <TableCell sx={bodyCellSx}>
                        {Math.floor(p.billable_seconds / 3600)}h {Math.floor((p.billable_seconds % 3600) / 60)}m
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">{formatCurrency(p.billable_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Case Summary ({cases.length} cases)
              </Typography>
              {cases.length > 0 && !isLimited && (
                <Button size="small" onClick={toggleAll}>
                  {expandedCases.size === cases.length ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {cases.length === 0 ? (
              <Alert severity="info">No time-based case data found for the selected filters.</Alert>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow>
                      {!isLimited && <TableCell sx={headerCellSx} />}
                      <TableCell sx={headerCellSx}>Case</TableCell>
                      <TableCell sx={headerCellSx}>File #</TableCell>
                      <TableCell sx={headerCellSx}>Client</TableCell>
                      <TableCell sx={headerCellSx}>Project</TableCell>
                      <TableCell sx={headerCellSx} align="right">Rate</TableCell>
                      {!isLimited && <TableCell sx={headerCellSx}>Clock</TableCell>}
                      <TableCell sx={headerCellSx}>Billable</TableCell>
                      {!isLimited && <TableCell sx={headerCellSx}>Billable %</TableCell>}
                      <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                      <TableCell sx={headerCellSx} align="right">Uninvoiced</TableCell>
                      <TableCell sx={headerCellSx}>Invoiced</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCases.map((row) => (
                      <CaseRow
                        key={row.case_id}
                        row={row}
                        expanded={expandedCases.has(row.case_id)}
                        onToggle={() => toggleCase(row.case_id)}
                        navigate={navigate}
                        limited={isLimited}
                      />
                    ))}
                    <TableRow>
                      <TableCell sx={totalCellSx} colSpan={isLimited ? 5 : 6}>Totals</TableCell>
                      {!isLimited && <TableCell sx={totalCellSx}>{summary.clock_human}</TableCell>}
                      <TableCell sx={totalCellSx}>{summary.billable_human}</TableCell>
                      {!isLimited && <TableCell sx={totalCellSx}>{summary.billable_percent ?? 0}%</TableCell>}
                      <TableCell sx={totalCellSx} align="right">{formatCurrency(summary.total_billable_amount)}</TableCell>
                      <TableCell sx={totalCellSx} align="right">{formatCurrency(summary.uninvoiced_amount)}</TableCell>
                      <TableCell sx={totalCellSx} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MonthlyCaseBillabilityReport;
